/* Careers page — `.lpv3` redesign. All data/logic (positions fetch, apply
 * flow, admin add/delete, copy-to-clipboard) is preserved; only the static
 * page chrome is restyled to match the landing/reseller design. */
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Briefcase, CheckCircle2, Loader2, Upload, Plus } from "lucide-react";
import { Lpv3Header, Lpv3Footer } from "@/components/Lpv3Chrome";
import axios from "axios";
import "./LandingPageV3.css";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VALUES = [
  { icon: "ti-sparkles", title: "Innovate Boldly", desc: "We build AI-powered tools that are reshaping how India goes public." },
  { icon: "ti-target", title: "Ship & Iterate", desc: "Move fast, learn faster. Every feature we ship reaches real users immediately." },
  { icon: "ti-heart", title: "People First", desc: "We invest in our team's growth, well-being, and career trajectory." },
  { icon: "ti-bulb", title: "Think Like an Owner", desc: "Every team member has a voice. Your ideas shape the product and the company." },
];

const CareersPage = () => {
  const navigate = useNavigate();
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [applyingTo, setApplyingTo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showAddPosition, setShowAddPosition] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const fileRef = useRef(null);

  const [form, setForm] = useState({
    name: "", email: "", phone: "", date_of_joining: "",
    sales_experience: "", accounting_experience: "", current_address: ""
  });
  const [cvFile, setCvFile] = useState(null);

  const [newPos, setNewPos] = useState({
    title: "", department: "", location: "", type: "Full-time",
    experience: "", description: "", requirements: "", responsibilities: "",
    tags: "", freshers_welcome: false
  });

  useEffect(() => {
    fetchPositions();
    checkAdmin();
  }, []);

  const fetchPositions = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/careers/positions`);
      setPositions(res.data.positions);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const checkAdmin = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
      setIsAdmin(res.data.is_admin);
    } catch { setIsAdmin(false); }
  };

  const handleApply = async () => {
    if (!form.name || !form.email || !form.date_of_joining) {
      toast.error("Please fill name, email, and date of joining");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (cvFile) fd.append("cv", cvFile);
      await axios.post(`${API_URL}/api/careers/apply/${applyingTo.position_id}`, fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Application submitted successfully!");
      setApplyingTo(null);
      setForm({ name: "", email: "", phone: "", date_of_joining: "", sales_experience: "", accounting_experience: "", current_address: "" });
      setCvFile(null);
      fetchPositions();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit application");
    }
    setSubmitting(false);
  };

  const handleAddPosition = async () => {
    if (!newPos.title || !newPos.department || !newPos.location) {
      toast.error("Title, department, and location are required");
      return;
    }
    setAddLoading(true);
    try {
      await axios.post(`${API_URL}/api/careers/positions`, {
        ...newPos,
        requirements: newPos.requirements.split("\n").filter(Boolean),
        responsibilities: newPos.responsibilities.split("\n").filter(Boolean),
        tags: newPos.tags.split(",").map(t => t.trim()).filter(Boolean)
      }, { withCredentials: true });
      toast.success("Position added!");
      setShowAddPosition(false);
      setNewPos({ title: "", department: "", location: "", type: "Full-time", experience: "", description: "", requirements: "", responsibilities: "", tags: "", freshers_welcome: false });
      fetchPositions();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add position");
    }
    setAddLoading(false);
  };

  const handleDeletePosition = async (posId) => {
    try {
      await axios.delete(`${API_URL}/api/careers/positions/${posId}`, { withCredentials: true });
      toast.success("Position removed");
      fetchPositions();
    } catch (e) { toast.error("Failed to remove"); }
  };

  const copyPositionText = (pos) => {
    const text = `🚀 We're Hiring: ${pos.title}\n\n` +
      `📍 ${pos.location}\n⏰ ${pos.type} | ${pos.experience}\n🏢 ${pos.department}\n\n` +
      `${pos.description}\n\n` +
      `Requirements:\n${(pos.requirements || []).map(r => `• ${r}`).join("\n")}\n\n` +
      `Responsibilities:\n${(pos.responsibilities || []).map(r => `• ${r}`).join("\n")}\n\n` +
      (pos.freshers_welcome ? `✅ Freshers Welcome to Apply!\n\n` : "") +
      `Apply now: ${window.location.origin}/careers\n\n` +
      `#Hiring #IPOLabs #${pos.tags?.join(" #") || "Jobs"}`;
    navigator.clipboard.writeText(text);
    toast.success("Job posting copied to clipboard — paste on LinkedIn, Naukri, Indeed, etc.");
  };

  const adminAddBtn = isAdmin ? (
    <button onClick={() => setShowAddPosition(true)} data-testid="add-position-btn" className="hd-login"
      style={{ border: ".5px solid #6C4BF4", color: "#6C4BF4", borderRadius: 20, padding: "6px 13px" }}>
      + Add Position
    </button>
  ) : null;

  return (
    <div className="lpv3" data-testid="careers-page">
      <Seo
        title="Careers at IPOLabs | Join India's IPO Platform"
        description="Explore open roles at IPOLabs and help build India's end-to-end IPO management platform for founders, advisors and capital markets."
        path="/careers"
      />
      <Lpv3Header rightSlot={adminAddBtn} />

      {/* HERO */}
      <div className="hero compact" data-testid="careers-hero">
        <span className="hero-pill">Join the team building India&apos;s IPO Operating System</span>
        <div className="hero-h">Build the Future of <span className="accent">Indian Capital Markets</span></div>
        <div className="hero-sub hero-sub-wide">We&apos;re a small, high-impact team transforming how companies go public in India. If you&apos;re driven by ambition and want to make a real dent in the $100B+ IPO ecosystem, we want to talk.</div>
        <div className="hero-btns">
          <button className="btn-dark" onClick={() => document.getElementById("positions")?.scrollIntoView({ behavior: "smooth" })} data-testid="careers-view-positions"><i className="ti ti-briefcase" aria-hidden="true" /> View Open Positions</button>
        </div>
      </div>

      {/* VALUES */}
      <div className="sec">
        <div className="sec-kick">Our Values</div>
        <div className="sec-h">What drives us at <span className="accent">IPOLabs</span></div>
        <div className="sec-d">Four principles that shape how we build, ship and grow together.</div>
        <div className="vals">
          {VALUES.map((v, i) => (
            <div className="val" key={i} data-testid={`value-${i}`}>
              <div className="val-ic"><i className={`ti ${v.icon}`} aria-hidden="true" /></div>
              <div className="val-h">{v.title}</div>
              <div className="val-d">{v.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* OPEN POSITIONS */}
      <div className="sec alt" id="positions">
        <div className="sec-kick">Careers</div>
        <div className="sec-h">Open Positions</div>
        <div className="sec-d">{positions.length} role{positions.length !== 1 ? "s" : ""} available — find where you fit in.</div>

        {loading ? (
          <div style={{ textAlign: "center", padding: "40px 0" }}><Loader2 className="w-6 h-6 animate-spin" style={{ color: "#6C4BF4", margin: "0 auto" }} /></div>
        ) : positions.length === 0 ? (
          <div className="res-empty">
            <i className="ti ti-briefcase" style={{ fontSize: 40 }} aria-hidden="true" />
            <p style={{ fontSize: 17, marginTop: 10 }}>No open positions right now</p>
            <p style={{ fontSize: 14 }}>Check back soon — we&apos;re always growing!</p>
          </div>
        ) : (
          <div>
            {positions.map((pos) => {
              const expanded = expandedId === pos.position_id;
              return (
                <div className="car-pos" key={pos.position_id} data-testid={`position-${pos.position_id}`}>
                  <div className="car-pos-head" onClick={() => setExpandedId(expanded ? null : pos.position_id)}>
                    <div style={{ flex: 1 }}>
                      <div className="car-pos-h">{pos.title}{pos.freshers_welcome && <span className="car-fresh">Freshers Welcome</span>}</div>
                      <div className="car-meta">
                        <span><i className="ti ti-building" aria-hidden="true" />{pos.department}</span>
                        <span><i className="ti ti-map-pin" aria-hidden="true" />{pos.location}</span>
                        <span><i className="ti ti-clock" aria-hidden="true" />{pos.type}</span>
                        <span><i className="ti ti-briefcase" aria-hidden="true" />{pos.experience}</span>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <button className="icon-btn" title="Copy for LinkedIn/Job boards" onClick={(e) => { e.stopPropagation(); copyPositionText(pos); }}>
                        <i className="ti ti-copy" aria-hidden="true" />
                      </button>
                      {isAdmin && (
                        <button className="icon-btn danger" title="Close position" onClick={(e) => { e.stopPropagation(); handleDeletePosition(pos.position_id); }}>
                          <i className="ti ti-trash" aria-hidden="true" />
                        </button>
                      )}
                      <i className={`ti ${expanded ? "ti-chevron-up" : "ti-chevron-down"}`} style={{ color: "#9a9aa4", fontSize: 20 }} aria-hidden="true" />
                    </div>
                  </div>

                  {expanded && (
                    <div className="car-pos-body">
                      {pos.freshers_welcome && (
                        <div className="car-fresh-box">Freshers are welcome to apply! We value enthusiasm, willingness to learn, and a strong foundation in Commerce/Accounts over years of experience.</div>
                      )}
                      <div className="car-sec-h">About the Role</div>
                      <p style={{ fontSize: 14, color: "#55555f", lineHeight: 1.6 }}>{pos.description}</p>

                      {pos.requirements?.length > 0 && (
                        <>
                          <div className="car-sec-h">Requirements</div>
                          {pos.requirements.map((r, i) => (
                            <div className="car-li" key={i}><i className="ti ti-circle-check" aria-hidden="true" /><span>{r}</span></div>
                          ))}
                        </>
                      )}

                      {pos.responsibilities?.length > 0 && (
                        <>
                          <div className="car-sec-h">Responsibilities</div>
                          {pos.responsibilities.map((r, i) => (
                            <div className="car-li resp" key={i}><i className="ti ti-arrow-right" aria-hidden="true" /><span>{r}</span></div>
                          ))}
                        </>
                      )}

                      {pos.tags?.length > 0 && (
                        <div style={{ marginTop: 12 }}>
                          {pos.tags.map((t, i) => <span key={i} className="ab-tag" style={{ marginRight: 6 }}>{t}</span>)}
                        </div>
                      )}

                      <div className="car-actions">
                        <button className="btn-dark" onClick={() => setApplyingTo(pos)} data-testid={`apply-btn-${pos.position_id}`}>Apply Now <i className="ti ti-arrow-right" aria-hidden="true" /></button>
                        <button className="btn-line" onClick={() => copyPositionText(pos)}><i className="ti ti-copy" aria-hidden="true" /> Copy for Job Boards</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* CTA */}
      <div className="cta" data-testid="careers-cta">
        <div className="cta-h">Don&apos;t see your role?</div>
        <div className="cta-d">We&apos;re always looking for exceptional talent. Send your resume and we&apos;ll keep you in mind for future openings.</div>
        <div className="hero-btns">
          <a href="mailto:founders.ipolabs@gmail.com?subject=Speculative Application — IPOLabs">
            <button className="btn-dark"><i className="ti ti-mail" aria-hidden="true" /> Send Resume</button>
          </a>
        </div>
      </div>

      <Lpv3Footer />

      {/* Apply Dialog */}
      <Dialog open={!!applyingTo} onOpenChange={() => setApplyingTo(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Briefcase className="w-5 h-5 text-[#6C4BF4]" /> Apply — {applyingTo?.title}</DialogTitle>
            <DialogDescription>{applyingTo?.department} &middot; {applyingTo?.location}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Full Name *</Label>
                <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Your full name" data-testid="apply-name" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Email *</Label>
                <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="you@email.com" data-testid="apply-email" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Phone</Label>
                <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="+91 98765 43210" data-testid="apply-phone" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Earliest Date of Joining *</Label>
                <Input type="date" value={form.date_of_joining} onChange={e => setForm({...form, date_of_joining: e.target.value})} data-testid="apply-doj" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Total Years of Sales Experience</Label>
                <Input type="number" min="0" value={form.sales_experience} onChange={e => setForm({...form, sales_experience: e.target.value})} placeholder="0" data-testid="apply-sales-exp" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Total Years of Accounting Experience</Label>
                <Input type="number" min="0" value={form.accounting_experience} onChange={e => setForm({...form, accounting_experience: e.target.value})} placeholder="0" data-testid="apply-acc-exp" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Current Residing Address</Label>
              <Textarea value={form.current_address} onChange={e => setForm({...form, current_address: e.target.value})} placeholder="Your full current address" rows={2} data-testid="apply-address" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Upload CV / Resume *</Label>
              <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center cursor-pointer hover:border-[#6C4BF4] transition-colors" onClick={() => fileRef.current?.click()}>
                {cvFile ? (
                  <div className="flex items-center justify-center gap-2 text-sm text-green-700"><CheckCircle2 className="w-4 h-4" /> {cvFile.name}</div>
                ) : (
                  <div className="text-muted-foreground"><Upload className="w-6 h-6 mx-auto mb-1 text-gray-400" /><p className="text-xs">Click to upload PDF, DOC, or DOCX</p></div>
                )}
                <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={e => setCvFile(e.target.files?.[0] || null)} data-testid="apply-cv-input" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApplyingTo(null)}>Cancel</Button>
            <Button onClick={handleApply} disabled={submitting} className="bg-[#6C4BF4] hover:bg-[#5a3fd6] gap-2" data-testid="submit-application-btn">
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</> : <><CheckCircle2 className="w-4 h-4" /> Submit Application</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Position Dialog */}
      <Dialog open={showAddPosition} onOpenChange={setShowAddPosition}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Plus className="w-5 h-5 text-[#6C4BF4]" /> Add New Position</DialogTitle>
            <DialogDescription>Create a new job listing</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Job Title *</Label><Input value={newPos.title} onChange={e => setNewPos({...newPos, title: e.target.value})} placeholder="e.g., Product Manager" data-testid="new-pos-title" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Department *</Label><Input value={newPos.department} onChange={e => setNewPos({...newPos, department: e.target.value})} placeholder="e.g., Product" data-testid="new-pos-dept" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Location *</Label><Input value={newPos.location} onChange={e => setNewPos({...newPos, location: e.target.value})} placeholder="Mumbai, WFO" data-testid="new-pos-location" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Experience</Label><Input value={newPos.experience} onChange={e => setNewPos({...newPos, experience: e.target.value})} placeholder="0-2 Years" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Type</Label>
                <Select value={newPos.type} onValueChange={v => setNewPos({...newPos, type: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Full-time">Full-time</SelectItem><SelectItem value="Part-time">Part-time</SelectItem><SelectItem value="Internship">Internship</SelectItem><SelectItem value="Contract">Contract</SelectItem></SelectContent></Select>
              </div>
              <div className="flex items-end pb-1 gap-3">
                <Label className="text-xs">Freshers Welcome?</Label>
                <Switch checked={newPos.freshers_welcome} onCheckedChange={v => setNewPos({...newPos, freshers_welcome: v})} />
              </div>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Description</Label><Textarea value={newPos.description} onChange={e => setNewPos({...newPos, description: e.target.value})} rows={3} placeholder="About the role..." /></div>
            <div className="space-y-1.5"><Label className="text-xs">Requirements (one per line)</Label><Textarea value={newPos.requirements} onChange={e => setNewPos({...newPos, requirements: e.target.value})} rows={3} placeholder={"Bachelor's degree in...\n2+ years experience in..."} /></div>
            <div className="space-y-1.5"><Label className="text-xs">Responsibilities (one per line)</Label><Textarea value={newPos.responsibilities} onChange={e => setNewPos({...newPos, responsibilities: e.target.value})} rows={3} placeholder={"Lead product strategy...\nCollaborate with engineering..."} /></div>
            <div className="space-y-1.5"><Label className="text-xs">Tags (comma-separated)</Label><Input value={newPos.tags} onChange={e => setNewPos({...newPos, tags: e.target.value})} placeholder="Product, Strategy, SaaS" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddPosition(false)}>Cancel</Button>
            <Button onClick={handleAddPosition} disabled={addLoading} className="bg-[#6C4BF4] hover:bg-[#5a3fd6] gap-2" data-testid="save-position-btn">
              {addLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add Position
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CareersPage;
